import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { EmptyState } from '@redhand/ui';
import {
  AlertTriangle,
  Camera,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Star,
  Trash2,
  Upload,
  Wand2,
  X,
} from 'lucide-react';
import type { MessageKey } from '@reos/i18n';
import { useT } from '../lib/i18n.js';
import { PhotoStagingPanel } from './PhotoStagingPanel.js';
import { resizeImageOrOriginal } from '../lib/resize-image.js';
import {
  deletePropertyMedia,
  errorMessage,
  fetchPropertyMedia,
  fetchPropertyMediaSuggestion,
  mediaSrc,
  reorderPropertyMedia,
  setPropertyMediaCaption,
  setPropertyMediaCover,
  uploadPropertyMedia,
  type PropertyMediaItem,
  type PropertyMediaSuggestionItem,
} from '../lib/properties.js';

/**
 * Sekce „Fotografie" (krok O6): galerie nemovitosti s lightboxem, zmenou
 * poradi, titulni fotkou, mazanim a nahravanim.
 *
 * Krok MOB1 bod 4 — „Vyfotit": na telefonu vedle „Nahrát fotky" druhe
 * tlacitko, ktere otevre rovnou fotoaparat (`capture="environment"`), aby
 * makler na obhlidce nemusel chodit pres galerii. Na desktopu je schovane
 * (`rh-only-mobile`, sazba v `styles/ux-v4.css`) — tam fotoaparat nikdo
 * nepouziva a `capture` by jen zmatlo vyber souboru.
 *
 * JEDNA komponenta pro nemovitost i zakazku (`nemovitost-detail.tsx`,
 * `zakazka-detail.tsx`), takze „Vyfotit" je obou mistech bez druhe kopie.
 *
 * ROZHODNUTI O6: poradi se meni nativnim HTML5 drag&drop plus sipkami
 * („posunout vlevo/vpravo"). `@dnd-kit/sortable` v zavislostech neni a kvuli
 * jedne mrizce se nepridava; sipky navic delaji preskladani dostupne
 * z klavesnice, coz samotny drag nikdy neumi.
 *
 * Obrazky se tahnou pres API stream (`credentials`), proto `crossOrigin`
 * — v dev rezimu bezi UI a API na jinem portu.
 */

export interface PropertyGalleryProps {
  readonly propertyId: string;
  /** false = jen prohlizeni (bez nahravani, mazani a preskladani). */
  readonly canEdit?: boolean;
  /**
   * Krok STG1 — zakazka, ve ktere galerie zije. `undefined` (karta
   * nemovitosti) = BEZ virtualniho stagingu: staging se pocita na inzeraty
   * a plati z kreditu kancelare, takze bez zakazky nema kam patrit ani
   * z ceho cerpat.
   */
  readonly caseId?: string;
}

type Busy = { readonly kind: 'idle' } | { readonly kind: 'busy' } | { readonly kind: 'error'; readonly message: string };

/**
 * Krok NAP3 bod 1 — VAROVANI KVALITY U FOTKY. Kody meri server pri nahrani
 * (`assessPhotoQuality`, @reos/ai); UI z nich sklada vetu z katalogu, protoze
 * odpoved API zadny text nenese (CLAUDE.md pravidlo 5).
 */
const QUALITY_ISSUE_KEYS = {
  too_dark: 'webAdmin.gallery.qualityTooDark',
  blurry: 'webAdmin.gallery.qualityBlurry',
  tilted_horizon: 'webAdmin.gallery.qualityTiltedHorizon',
} as const satisfies Readonly<Record<string, MessageKey>>;

function moved(items: readonly PropertyMediaItem[], from: number, to: number): PropertyMediaItem[] {
  const next = [...items];
  const [picked] = next.splice(from, 1);
  if (picked === undefined) return next;
  next.splice(Math.max(0, Math.min(next.length, to)), 0, picked);
  return next;
}

export function PropertyGallery({ propertyId, canEdit = true, caseId }: PropertyGalleryProps): React.JSX.Element {
  const t = useT();
  const [items, setItems] = React.useState<readonly PropertyMediaItem[] | null>(null);
  const [state, setState] = React.useState<Busy>({ kind: 'idle' });
  const [lightbox, setLightbox] = React.useState<number | null>(null);
  const [dragIndex, setDragIndex] = React.useState<number | null>(null);
  /** Krok NAP3 — navrh ze serveru, ktery jeste makler nepotvrdil. */
  const [suggestion, setSuggestion] = React.useState<readonly PropertyMediaSuggestionItem[] | null>(
    null,
  );
  const fileInput = React.useRef<HTMLInputElement>(null);

  const load = React.useCallback(() => {
    void fetchPropertyMedia(propertyId)
      .then((data) => {
        setItems(data);
      })
      .catch((error: unknown) => {
        setItems([]);
        setState({ kind: 'error', message: errorMessage(error) });
      });
  }, [propertyId]);

  React.useEffect(load, [load]);

  const run = (work: () => Promise<unknown>): void => {
    setState({ kind: 'busy' });
    void work()
      .then(() => {
        setState({ kind: 'idle' });
        load();
      })
      .catch((error: unknown) => {
        setState({ kind: 'error', message: errorMessage(error) });
        load();
      });
  };

  /**
   * `resize` = fotka z fotoaparatu. Telefon dnes fotí 12 Mpx / 5 MB; na
   * mobilnim pripojeni by nahrani trvalo minuty a server stejne dela nahledy.
   * Soubory vybrane z pocitace se NEZMENSUJI — tam uzivatel vedome vybira,
   * co nahrava (treba podklad pro inzerat ve vysokem rozliseni).
   */
  const handleUpload = (files: FileList | null, resize = false): void => {
    if (files === null || files.length === 0) return;
    run(async () => {
      const picked = resize
        ? await Promise.all(Array.from(files).map((file) => resizeImageOrOriginal(file)))
        : Array.from(files);
      const result = await uploadPropertyMedia(propertyId, picked);
      if (result.rejected.length > 0) {
        throw new Error(
          t('webAdmin.gallery.uploadRejected', {
            files: result.rejected.map((r) => r.fileName).join(', '),
          }),
        );
      }
    });
  };

  const applyOrder = (next: readonly PropertyMediaItem[]): void => {
    setItems(next);
    run(() => reorderPropertyMedia(propertyId, next.map((item) => item.id)));
  };

  const move = (index: number, delta: number): void => {
    if (items === null) return;
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    applyOrder(moved(items, index, target));
  };

  /**
   * Krok NAP3 bod 1 — „Navrhnout pořadí a popisky". Navrh pocita SERVER
   * (`GET …/media/suggestion`) z mistnosti u fotek a z vysledku kontroly
   * kvality; tady se jen ukaze a ceka se na potvrzeni. Dokud makler nepotvrdi,
   * v galerii se nic nezmeni.
   */
  const askSuggestion = (): void => {
    setState({ kind: 'busy' });
    void fetchPropertyMediaSuggestion(propertyId)
      .then((proposal) => {
        setSuggestion(proposal);
        setState({ kind: 'idle' });
      })
      .catch((error: unknown) => {
        setState({ kind: 'error', message: errorMessage(error) });
      });
  };

  /** Potvrzeni navrhu: poradi jednim zapisem, popisky jen tam, kde chybi. */
  const applySuggestion = (): void => {
    const proposal = suggestion;
    if (proposal === null) return;
    setSuggestion(null);
    run(async () => {
      await reorderPropertyMedia(
        propertyId,
        proposal.map((entry) => entry.mediaId),
      );
      for (const entry of proposal) {
        if (entry.caption !== null) {
          await setPropertyMediaCaption(propertyId, entry.mediaId, entry.caption);
        }
      }
    });
  };

  const list = items ?? [];
  const current = lightbox === null ? undefined : list[lightbox];
  const itemById = (mediaId: string): PropertyMediaItem | undefined =>
    list.find((item) => item.id === mediaId);

  /** Vady fotky jako hotove vety; prazdne pole = fotka merena a v poradku. */
  const qualityWarnings = (item: PropertyMediaItem): readonly string[] =>
    (item.qualityIssues ?? [])
      .map((code) => QUALITY_ISSUE_KEYS[code as keyof typeof QUALITY_ISSUE_KEYS])
      .filter((key) => key !== undefined)
      .map((key) => t(key));

  return (
    <div data-testid="property-gallery">
      {canEdit ? (
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="rh-btn"
            onClick={() => fileInput.current?.click()}
            disabled={state.kind === 'busy'}
            data-testid="gallery-upload"
          >
            <Upload size={16} aria-hidden="true" /> {t('webAdmin.gallery.uploadButton')}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif,image/gif"
            multiple
            className="hidden"
            aria-label={t('webAdmin.gallery.uploadButton')}
            data-testid="gallery-file-input"
            onChange={(event) => {
              handleUpload(event.target.value === '' ? null : event.target.files);
              event.target.value = '';
            }}
          />
          {/* Krok MOB1 bod 4 — fotoaparat telefonu. `capture` je standardni
              atribut `<input type="file">`, takze stejne funguje i v nativnim
              obalu (Capacitor) — zadna funkce jen pro prohlizec. */}
          <label
            className="rh-btn rh-only-mobile"
            data-testid="gallery-capture"
            title={t('webAdmin.gallery.captureHint')}
            style={{ opacity: state.kind === 'busy' ? 0.5 : 1 }}
          >
            <Camera size={16} aria-hidden="true" /> {t('webAdmin.gallery.captureButton')}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              disabled={state.kind === 'busy'}
              aria-label={t('webAdmin.gallery.captureButton')}
              data-testid="gallery-capture-input"
              onChange={(event) => {
                handleUpload(event.target.value === '' ? null : event.target.files, true);
                event.target.value = '';
              }}
            />
          </label>
          {/* Krok NAP3 bod 1 — navrh poradi a popisku. Bez fotek nema co radit. */}
          <button
            type="button"
            className="rh-btn"
            onClick={askSuggestion}
            disabled={state.kind === 'busy' || list.length === 0}
            title={t('webAdmin.gallery.suggestHint')}
            data-testid="gallery-suggest"
          >
            <Wand2 size={16} aria-hidden="true" /> {t('webAdmin.gallery.suggestButton')}
          </button>
          <span className="text-sm" style={{ color: 'var(--rh-text-muted)' }}>
            {t('webAdmin.gallery.uploadHint')}
          </span>
        </div>
      ) : null}

      {state.kind === 'busy' ? <p role="status">{t('webAdmin.gallery.working')}</p> : null}
      {state.kind === 'error' ? (
        <p role="alert" className="mb-3 text-sm" style={{ color: 'var(--rh-danger)' }} data-testid="gallery-error">
          {state.message}
        </p>
      ) : null}

      {items === null ? (
        <p role="status">{t('webAdmin.gallery.loading')}</p>
      ) : list.length === 0 ? (
        <EmptyState
          title={t('webAdmin.gallery.emptyTitle')}
          description={t('webAdmin.gallery.emptyDescription')}
        />
      ) : (
        <ul
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 m-0 p-0 list-none"
          aria-label={t('webAdmin.gallery.ariaLabel')}
        >
          {list.map((item, index) => (
            <li
              key={item.id}
              className="rounded-lg overflow-hidden border relative"
              style={{ borderColor: item.isCover ? 'var(--rh-accent)' : 'var(--rh-border)' }}
              draggable={canEdit}
              data-testid={`gallery-item-${item.id}`}
              onDragStart={() => { setDragIndex(index); }}
              onDragEnd={() => { setDragIndex(null); }}
              onDragOver={(event) => { if (canEdit && dragIndex !== null) event.preventDefault(); }}
              onDrop={(event) => {
                event.preventDefault();
                if (!canEdit || dragIndex === null || dragIndex === index) return;
                applyOrder(moved(list, dragIndex, index));
                setDragIndex(null);
              }}
            >
              <button
                type="button"
                className="block w-full"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'zoom-in' }}
                onClick={() => { setLightbox(index); }}
                aria-label={t('webAdmin.gallery.openPhoto', { name: item.caption ?? item.name ?? '' })}
              >
                <img
                  src={mediaSrc(item.thumbUrl)}
                  crossOrigin="use-credentials"
                  alt={item.caption ?? item.name ?? t('webAdmin.gallery.photoAlt')}
                  className="w-full h-32 object-cover"
                  loading="lazy"
                />
              </button>
              {item.isCover ? (
                <span className="rh-badge rh-badge--neutral absolute top-1 left-1" data-testid={`gallery-cover-${item.id}`}>
                  {t('webAdmin.gallery.coverBadge')}
                </span>
              ) : null}
              {/*
                Krok NAP3 bod 1 — VAROVANI KVALITY U TE FOTKY, ke ktere patri.
                Jen varuje: fotku nemaze ani neschovava, protoze i tmavy sklep
                nebo rozmazany detail muze byt zamerne nahrany podklad.
              */}
              {qualityWarnings(item).length > 0 ? (
                <p
                  className="m-0 px-1 py-1 text-xs"
                  style={{ color: 'var(--rh-danger)' }}
                  data-testid={`gallery-quality-${item.id}`}
                >
                  <AlertTriangle size={12} aria-hidden="true" />{' '}
                  {qualityWarnings(item).join(' · ')}
                </p>
              ) : null}
              {canEdit ? (
                /*
                  Krok STG1: `flex-wrap`. Rada akci se do STG1 vesla na jeden
                  radek, protoze jich bylo pet; sesta („Virtuálně zařídit")
                  uz na uzkou dlazdici (galerie zakazky kresli ctyri sloupce
                  v karte) nevysla a `overflow-hidden` na dlazdici ji uriznula
                  — tlacitko bylo v DOM, ale makler na nej nedosahl. Zalomeni
                  je mensi zasah nez jina mrizka a plati pro vsechny akce,
                  takze dalsi pribyla akce se chova stejne.
                */
                <div className="flex items-center justify-between flex-wrap gap-1 px-1 py-1">
                  <span className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      className="rh-btn"
                      onClick={() => { move(index, -1); }}
                      disabled={index === 0 || state.kind === 'busy'}
                      aria-label={t('webAdmin.gallery.moveLeft')}
                      title={t('webAdmin.gallery.moveLeft')}
                    >
                      <ChevronLeft size={14} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="rh-btn"
                      onClick={() => { move(index, 1); }}
                      disabled={index === list.length - 1 || state.kind === 'busy'}
                      aria-label={t('webAdmin.gallery.moveRight')}
                      title={t('webAdmin.gallery.moveRight')}
                    >
                      <ChevronRight size={14} aria-hidden="true" />
                    </button>
                  </span>
                  <span className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      className="rh-btn"
                      onClick={() => { run(() => setPropertyMediaCover(propertyId, item.id)); }}
                      disabled={item.isCover || state.kind === 'busy'}
                      aria-label={t('webAdmin.gallery.setCover')}
                      title={t('webAdmin.gallery.setCover')}
                      data-testid={`gallery-set-cover-${item.id}`}
                    >
                      <Star size={14} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="rh-btn"
                      onClick={() => { run(() => deletePropertyMedia(propertyId, item.id)); }}
                      disabled={state.kind === 'busy'}
                      aria-label={t('webAdmin.gallery.deletePhoto')}
                      title={t('webAdmin.gallery.deletePhoto')}
                      data-testid={`gallery-delete-${item.id}`}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                    {/*
                      Krok STG1 — „Virtuálně zařídit (AI)". Jen u zakazky
                      (viz `caseId` v props) a jen u ORIGINALU: vizualizace
                      uz AI nabytek ma a druhe kolo by jen rozmazavalo, co je
                      skutecny stav.
                    */}
                    {caseId !== undefined && item.isVisualization !== true ? (
                      <PhotoStagingPanel
                        caseId={caseId}
                        propertyId={propertyId}
                        mediaId={item.id}
                        onChanged={load}
                      />
                    ) : null}
                  </span>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {/*
        Krok NAP3 bod 1 — NAVRH K POTVRZENI. Makler vidi, co se stane, driv nez
        se to stane: poradi i navrzene popisky. „Zavřít" navrh zahodi.
      */}
      <Dialog.Root
        open={suggestion !== null}
        onOpenChange={(open) => {
          if (!open) setSuggestion(null);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60" />
          <Dialog.Content
            className="fixed top-10 left-1/2 -translate-x-1/2 rounded-lg shadow-xl p-4 w-full max-w-xl"
            style={{ background: 'var(--rh-card)', maxHeight: '80vh', overflowY: 'auto' }}
            data-testid="gallery-suggest-dialog"
          >
            <Dialog.Title className="text-base font-semibold">
              {t('webAdmin.gallery.suggestTitle')}
            </Dialog.Title>
            <Dialog.Description className="mb-2 text-sm" style={{ color: 'var(--rh-text-muted)' }}>
              {t('webAdmin.gallery.suggestDescription')}
            </Dialog.Description>
            <ol className="m-0 pl-5 text-sm" data-testid="gallery-suggest-list">
              {(suggestion ?? []).map((entry) => {
                const item = itemById(entry.mediaId);
                return (
                  <li key={entry.mediaId} data-testid={`gallery-suggest-item-${entry.mediaId}`}>
                    {entry.caption ?? item?.caption ?? item?.name ?? t('webAdmin.gallery.photoAlt')}
                    {entry.hasQualityIssue ? (
                      <span className="ml-1 text-xs" style={{ color: 'var(--rh-danger)' }}>
                        {t('webAdmin.gallery.suggestQualityNote')}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ol>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                className="rh-btn rh-btn--primary"
                onClick={applySuggestion}
                data-testid="gallery-suggest-apply"
              >
                {t('webAdmin.gallery.suggestApply')}
              </button>
              <Dialog.Close className="rh-btn" data-testid="gallery-suggest-close">
                {t('webAdmin.gallery.suggestCancel')}
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root open={current !== undefined} onOpenChange={(open) => { if (!open) setLightbox(null); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60" />
          <Dialog.Content
            className="fixed top-10 left-1/2 -translate-x-1/2 rounded-lg shadow-xl p-4 w-full max-w-3xl"
            style={{ background: 'var(--rh-card)' }}
            data-testid="gallery-lightbox"
          >
            <Dialog.Title className="text-base font-semibold">
              {current?.caption ?? current?.name ?? t('webAdmin.gallery.photoAlt')}
            </Dialog.Title>
            {current !== undefined ? (
              <img
                src={mediaSrc(current.url)}
                crossOrigin="use-credentials"
                alt={current.caption ?? current.name ?? t('webAdmin.gallery.photoAlt')}
                className="w-full max-h-[70vh] object-contain"
              />
            ) : null}
            <div className="mt-3 flex items-center justify-between">
              <button
                type="button"
                className="rh-btn"
                onClick={() => { setLightbox((i) => (i === null ? null : Math.max(0, i - 1))); }}
                disabled={lightbox === 0}
                aria-label={t('webAdmin.gallery.previousPhoto')}
              >
                <ChevronLeft size={16} aria-hidden="true" /> {t('webAdmin.gallery.previousPhoto')}
              </button>
              <span className="text-sm" style={{ color: 'var(--rh-text-muted)' }}>
                <ImageIcon size={14} aria-hidden="true" />{' '}
                {t('webAdmin.gallery.position', {
                  index: String((lightbox ?? 0) + 1),
                  total: String(list.length),
                })}
              </span>
              <button
                type="button"
                className="rh-btn"
                onClick={() => {
                  setLightbox((i) => (i === null ? null : Math.min(list.length - 1, i + 1)));
                }}
                disabled={lightbox === list.length - 1}
                aria-label={t('webAdmin.gallery.nextPhoto')}
              >
                {t('webAdmin.gallery.nextPhoto')} <ChevronRight size={16} aria-hidden="true" />
              </button>
            </div>
            <Dialog.Close className="rh-btn mt-3" aria-label={t('webAdmin.gallery.closePhoto')}>
              <X size={16} aria-hidden="true" /> {t('webAdmin.gallery.closePhoto')}
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
